import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  UserPlus,
  MoveRight,
  CheckCircle2,
  AlertTriangle,
  Pencil,
  Umbrella,
  MessageSquare,
  Archive,
  ArchiveRestore,
  Link2,
  Unlink2,
  ListChecks,
  Tag,
  Paperclip,
  SlidersHorizontal,
  CalendarPlus,
  CalendarClock,
  CalendarX,
} from 'lucide-react';
import { ROUTES } from '@/core/constants/routes';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { NotificationsSlotProvider } from '@/shared/contexts/notifications-slot-context';
import { NotificationCard } from '@/shared/components/custom/notification-card/NotificationCard';
import { InvitationCard, InvitationDialog, useMyInvitations } from '@/features/invitations/public';
import type { MyInvitation } from '@/features/invitations/public';
import { useOpenTask } from '@/features/tasks/public';
import { NotificationHubContext } from '../context/NotificationHubContext';
import { useNotifications } from '../hooks/useNotifications';
import { useMarkAsRead, useMarkAllAsRead } from '../hooks/useNotificationMutations';
import { useNotificationHub } from '../hooks/useNotificationHub';
import { NotificationList } from './NotificationList';
import type { NotificationListItem } from './NotificationList';
import type { NotificationsQueryParams, NotificationType } from '../types';

interface NotificationsProviderProps {
  children: ReactNode;
}

type FilterMode = 'all' | 'unread';

const KNOWN_NOTIFICATION_TYPES = new Set<string>([
  'task_assigned',
  'task_moved',
  'task_completed',
  'task_priority_changed',
  'task_updated',
  'task_comment_added',
  'task_archived',
  'task_unarchived',
  'task_dependency_added',
  'task_dependency_removed',
  'checklist_item_added',
  'tag_added',
  'attachment_uploaded',
  'field_value_upserted',
  'timeoff',
  'calendar_event_created',
  'calendar_event_updated',
  'calendar_event_cancelled',
]);

const CALENDAR_EVENT_NOTIFICATION_TYPES = new Set<string>([
  'calendar_event_created',
  'calendar_event_updated',
  'calendar_event_cancelled',
]);

function isNotificationType(type: string | null): type is NotificationType {
  return type !== null && KNOWN_NOTIFICATION_TYPES.has(type);
}

function isTaskNotificationType(type: string | null): boolean {
  return (
    isNotificationType(type) && type !== 'timeoff' && !CALENDAR_EVENT_NOTIFICATION_TYPES.has(type)
  );
}

function isCalendarEventNotificationType(type: string | null): boolean {
  return type !== null && CALENDAR_EVENT_NOTIFICATION_TYPES.has(type);
}

/** Returns a Lucide icon element matching the notification type. */
function NotificationTypeIcon({ type }: { type: string | null }) {
  const iconClass = 'h-4 w-4 text-primary';

  switch (type as NotificationType | null) {
    case 'task_assigned':
      return <UserPlus className={iconClass} aria-hidden="true" />;
    case 'task_moved':
      return <MoveRight className={iconClass} aria-hidden="true" />;
    case 'task_completed':
      return <CheckCircle2 className={iconClass} aria-hidden="true" />;
    case 'task_priority_changed':
      return <AlertTriangle className={iconClass} aria-hidden="true" />;
    case 'task_updated':
      return <Pencil className={iconClass} aria-hidden="true" />;
    case 'task_comment_added':
      return <MessageSquare className={iconClass} aria-hidden="true" />;
    case 'task_archived':
      return <Archive className={iconClass} aria-hidden="true" />;
    case 'task_unarchived':
      return <ArchiveRestore className={iconClass} aria-hidden="true" />;
    case 'task_dependency_added':
      return <Link2 className={iconClass} aria-hidden="true" />;
    case 'task_dependency_removed':
      return <Unlink2 className={iconClass} aria-hidden="true" />;
    case 'checklist_item_added':
      return <ListChecks className={iconClass} aria-hidden="true" />;
    case 'tag_added':
      return <Tag className={iconClass} aria-hidden="true" />;
    case 'attachment_uploaded':
      return <Paperclip className={iconClass} aria-hidden="true" />;
    case 'field_value_upserted':
      return <SlidersHorizontal className={iconClass} aria-hidden="true" />;
    case 'timeoff':
      return <Umbrella className={iconClass} aria-hidden="true" />;
    case 'calendar_event_created':
      return <CalendarPlus className={iconClass} aria-hidden="true" />;
    case 'calendar_event_updated':
      return <CalendarClock className={iconClass} aria-hidden="true" />;
    case 'calendar_event_cancelled':
      return <CalendarX className={iconClass} aria-hidden="true" />;
    default:
      return <Bell className={iconClass} aria-hidden="true" />;
  }
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);
  const setActiveOrganization = useOrganizationStore((s) => s.setActiveOrganization);
  const [filter, setFilter] = useState<FilterMode>('unread');
  const [selectedInvitation, setSelectedInvitation] = useState<MyInvitation | null>(null);

  // The feed is user-global — a notification's own tenant may not be the one
  // currently active. Switch context first so the target route/dialog fetches
  // against the right tenant instead of whatever org happens to be open.
  function ensureActiveOrganization(tenantId: string) {
    if (tenantId !== activeOrganizationId) setActiveOrganization(tenantId);
  }

  const notifParams: NotificationsQueryParams = filter === 'unread' ? { isRead: false } : {};
  const { data: notifData, isLoading } = useNotifications(notifParams);
  const { data: invitations = [] } = useMyInvitations();

  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const { connectionRef, status, reconnectCount } = useNotificationHub();
  const openTask = useOpenTask();

  const notifications = notifData?.items ?? [];
  const unreadNotifCount =
    filter === 'unread'
      ? (notifData?.totalCount ?? 0)
      : notifications.filter((n) => !n.isRead).length;
  const badgeCount = unreadNotifCount + invitations.length;

  const notifItems: NotificationListItem[] = notifications.map((n) => {
    const canOpenTimeOffRequest = n.type === 'timeoff' && n.entityId && n.tenantId;
    const canOpenTask = isTaskNotificationType(n.type) && !!n.entityId && !!n.tenantId;
    // No single-event GET endpoint exists yet — navigate to the calendar page
    // rather than attempting (and failing) to deep-link the specific event.
    const canOpenCalendar = isCalendarEventNotificationType(n.type) && !!n.tenantId;

    return {
      key: n.id ?? n.createdAt,
      timestamp: n.createdAt,
      node: (
        <div className="relative group">
          <NotificationCard
            avatar={
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <NotificationTypeIcon type={n.type} />
              </div>
            }
            title={isNotificationType(n.type) ? t(`notification.types.${n.type}`) : (n.title ?? '')}
            subtitle={n.message ?? ''}
            orgLabel={n.tenantName ?? undefined}
            timestamp={n.createdAt}
            isRead={n.isRead}
            onClick={
              canOpenTimeOffRequest
                ? () => {
                    ensureActiveOrganization(n.tenantId!);
                    navigate(ROUTES.timeOffRequest(n.tenantId!, n.entityId!));
                  }
                : canOpenTask
                  ? () => {
                      ensureActiveOrganization(n.tenantId!);
                      openTask(n.entityId!);
                    }
                  : canOpenCalendar
                    ? () => {
                        ensureActiveOrganization(n.tenantId!);
                        navigate(ROUTES.calendar(n.tenantId!));
                      }
                    : undefined
            }
            testId="notification-item"
          />
          {!n.isRead && n.id && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="notification-mark-read-btn"
                  aria-label={t('notification.markRead')}
                  onClick={() => markAsRead.mutate(n.id!)}
                  className="absolute right-2 top-2 h-6 w-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-muted hover:bg-muted/80 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Check className="h-3 w-3" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('notification.markRead')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      ),
    };
  });

  const invItems: NotificationListItem[] = invitations.map((inv) => ({
    key: `inv-${inv.id}`,
    timestamp: inv.createdAt,
    node: <InvitationCard invitation={inv} onOpen={() => setSelectedInvitation(inv)} />,
  }));

  const allItems = [...notifItems, ...invItems].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp),
  );

  const content = (
    <NotificationList
      items={allItems}
      isLoading={isLoading}
      hasMore={notifData?.hasNextPage ?? false}
      onLoadMore={() => {}}
      onMarkAllAsRead={() => markAllAsRead.mutate()}
      isMarkAllPending={markAllAsRead.isPending}
      filter={filter}
      onFilterChange={setFilter}
    />
  );

  return (
    <NotificationHubContext.Provider value={{ connectionRef, status, reconnectCount }}>
      <NotificationsSlotProvider content={content} count={badgeCount}>
        {children}
        <InvitationDialog
          invitation={selectedInvitation}
          open={selectedInvitation !== null}
          onClose={() => setSelectedInvitation(null)}
        />
      </NotificationsSlotProvider>
    </NotificationHubContext.Provider>
  );
}
